import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Col,
  Row,
  List,
  Divider,
  Avatar,
  Tooltip,
  Empty,
  Dropdown,
  Menu,
  notification,
  Select,
  Spin,
} from 'antd';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { sanitizeString } from 'utils/xss';
import { handleModalTreeSearchWrapper, addNewChildFromSearch } from 'helpers/contentlibrary';
import {
  GET_ALL_CATALOGS_LIBRARIES,
  SEARCH_ALL_DELIVERABLES,
} from 'graphql/queries/contentLibraryQueries';
import {
  CONTENT_LIBRARY_ADD_ITEM,
  CONTENT_LIBRARY_CATALOG_DELETE,
} from 'graphql/mutations/contentLibraryMutations';
import { userVar } from 'graphql/cache';
import { CATALOGS_LIMIT } from 'constants/index';
import { useContentLibrary } from 'context/ContentLibraryContext';
import {
  UnlinedTick,
  SliderIcon,
  DeleteIcon,
  AddBlockIcon,
  DownloadCircleIcon,
  ChevronRight,
  SaveIcon,
  PlusThinIcon,
  EmptyFileIcon,
} from 'components/Icons';

import Loader from 'components/Loader';
import Button from 'components/Button';
import Input from 'components/Input';
import ContentLibraryTree from 'pages/ContentLibrary/components/ContentLibraryTree/ContentLibraryTree';
import MoveLibraryModal from 'pages/ContentLibrary/ContentLibraryModals/MoveLibraryModal/MoveLibraryModal';
import images from 'constants/images';
import ConfirmModal from 'components/ConfirmModal';

const Prelist = forwardRef(
  (
    {
      language,
      chosen,
      tabType,
      prelist,
      addNewItem,
      addItem,
      removeItem,
      handleState,
      wixConnectedInstance,
      fetchMoreWixProductsAndServicesLoading,
      fetchMoreWixProductsAndServices,
      fetchMorePipedriveProductsLoading,
      fetchMorePipedriveProducts,
      fetchMoreHubSpotProductsLoading,
      fetchMoreHubSpotProducts,
      selectedFolder,
      setSelectedFolder,
      haveActiveIntegrations,
    },
    ref
  ) => {
    const {
      treeData,
      filterTreeData,
      selectedCatalogs,
      setIndexes,
      initialTreeData,
      addKey,
      addChild,
      saveDeliverable,
    } = useContentLibrary();

    /**
     * Filtered tree data
     */
    const filteredTreeData = filterTreeData(treeData, 'catalogs');

    const history = useHistory();

    const contentLibraryTreeRef = useRef(null);

    const [filteredprelist, setfilteredprelist] = useState(prelist);
    const [newPrelistItem, setNewPrelistItem] = useState(null);
    const [deletedItems, setDeletedItems] = useState([]);
    const [newItemName, setNewItemName] = useState('');
    const [cursor, setCursor] = useState(null);
    const [hasNext, setHasNext] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [selectedFolderLoading, setSelectedFolderLoading] = useState(false);
    const [emptyCatalog, setEmptyCatalog] = useState(false);
    const [tempChosen, setTempChosen] = useState('');
    const [showDuplicateCatalog, setShowDuplicateCatalog] = useState(false);
    const [selectedCatalog, setSelectedCatalog] = useState(null);
    const [confirmModal, setConfirmModal] = useState(false);
    const [selectedFolderKey, setSelectedFolderKey] = useState('');
    const [selectedFolderItemKey, setSelectedFolderItemKey] = useState('');
    const [modalTreeData, setModalTreeData] = useState({ children: [] });
    const [isRenamingCatalogItem, setIsRenamingCatalogItem] = useState(false);
    const [catalogs, setCatalogs] = useState([]);
    const [catalogsCount, setCatalogsCount] = useState(null);
    const [showNewDeliverablePopup, setShowNewDeliverablePopup] = useState(false);
    const [searchResults, setSearchResults] = useState(null);
    const [isDebouncing, setIsDebouncing] = useState(false);
    const [initialDeliverableName, setInitialDeliverableName] = useState('');

    // Use ref to preserve search results even when Select loses focus
    const searchResultsRef = useRef(null);

    // Create debounced version of filterDelivs to avoid multiple requests on each keystroke
    const debouncedFilterDelivsRef = useRef(null);

    // Ref to track if a tree click is happening to prevent blur from resetting
    const isTreeClickRef = useRef(false);
    const blurTimeoutRef = useRef(null);

    const user = useReactiveVar(userVar);

    /**
     * Filter items
     *
     * @param {Array} data
     * @param {Array} deletedItems
     */
    const filterItems = useCallback((data, deletedItems) => {
      // exclude deleted items
      const excludeNames = deletedItems.reduce((a, v) => ({ ...a, [v]: true }), {});

      return data
        .map((d) => {
          if (
            d.key === 'wixServices' ||
            d.key === 'wixProducts' ||
            d.key === 'defaultDeliverables' ||
            d.key === 'pipedriveProducts' ||
            d.key === 'hubspotProducts'
          ) {
            d[d.key] = filterItems(d[d.key], deletedItems);
          } else {
            if (d.editable || d.editableCatalog) {
              if (excludeNames[d.name]) {
                return null;
              }

              // dont include same item twice, so set the value in excludeNames list
              excludeNames[d.name] = true;
            }
          }

          return d;
        })
        .filter(Boolean);
    }, []);

    /**
     * Update filtered prelist
     *
     * @param {Array} data
     * @param {Array} deletedItemNames
     */
    const updatefilteredprelist = useCallback(
      (data, deletedItemNames = deletedItems) => {
        data = filterItems(data, deletedItemNames);

        if (newItemName.length && tabType !== 'delivs') {
          // add the newly added item
          let addedNewItem = { index: 0, name: null };

          data.forEach((d, index) => {
            if (d.key === 'defaultDeliverables') {
              addedNewItem = {
                index: index,
                key: 'defaultDeliverables',
                name: 'Default Deliverables',
              };
            }
          });

          // add last added item to list
          const arr = addedNewItem.name ? data[addedNewItem.index][addedNewItem.name] : data;
          const lastitem = arr?.[arr?.length - 1];

          if (lastitem && lastitem.name !== newItemName) {
            const newItem = {
              editable: true,
              selected: true,
              name: newItemName,
            };

            if (addedNewItem.name) {
              data[addedNewItem.index][addedNewItem.name].push(newItem);
            } else {
              data.push(newItem);
            }
          }
        }
        setSelectedFolderLoading(false);
        setfilteredprelist(data);
      },
      [deletedItems, filterItems, newItemName, tabType]
    );

    /**
     * Handle catalog data
     *
     * @param {Object} catalogsData
     */
    const handleCatalogData = useCallback(
      (catalogsData, selectedFolderData, append = false) => {
        const { catalogs: products, pagingMetadata } = catalogsData;

        if (append && products) {
          setCatalogs((prevCatalogs) => [...prevCatalogs, ...products]);
        } else {
          setCatalogs(products || []);
        }

        // Update cursor and hasNext from pagingMetadata
        if (pagingMetadata) {
          setCursor(pagingMetadata.cursor || null);
          setHasNext(pagingMetadata.hasNext || false);
        }

        // Keep catalogsCount for backward compatibility if needed
        if (catalogsData.catalogsCount !== undefined) {
          setCatalogsCount(catalogsData.catalogsCount);
        }

        const currentCatalogs = append ? [...catalogs, ...(products || [])] : products || [];

        let listProducts = currentCatalogs?.map((product) => ({
          ...product,
          editableCatalog: true,
        }));

        listProducts = listProducts.map((product) => {
          _.forOwn(chosen, (chosendata) =>
            chosendata.list?.forEach((l) => {
              l.name === product.name ? (product.selected = true) : '';
            })
          );
          return product;
        });

        selectedFolderData = selectedFolderData || selectedFolder;

        // Update selectedFolder with pagination metadata for catalog lists
        if (
          selectedFolderData.key &&
          ![
            'defaultDeliverables',
            'wixServices',
            'wixProducts',
            'pipedriveProducts',
            'hubspotProducts',
          ].includes(selectedFolderData.key)
        ) {
          setSelectedFolder({
            ...selectedFolderData,
            totalItems: catalogsCount || currentCatalogs.length,
            hasNext: pagingMetadata?.hasNext || false,
            cursor: pagingMetadata?.cursor || null,
          });
        }

        updatefilteredprelist([
          {
            name: selectedFolderData.name,
            key: selectedFolderData.key,
            [selectedFolderData.key]: listProducts,
            totalItems: catalogsCount || currentCatalogs.length,
            hasNext: pagingMetadata?.hasNext || false,
            cursor: pagingMetadata?.cursor || null,
          },
        ]);
      },
      [chosen, selectedFolder, updatefilteredprelist, catalogs, catalogsCount, setSelectedFolder]
    );

    const [fetchAllCatalogs, { refetch: refetchAllCatalogs, fetchMore }] = useLazyQuery(
      GET_ALL_CATALOGS_LIBRARIES,
      {
        fetchPolicy: 'no-cache',
        skip: !!catalogs?.length,
      }
    );

    const [searchAllDeliverables, { loading: searchAllLoading }] = useLazyQuery(
      SEARCH_ALL_DELIVERABLES,
      {
        fetchPolicy: 'no-cache',
      }
    );

    // Update modal tree data when context tree data changes
    useEffect(() => {
      setModalTreeData(filteredTreeData);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [treeData]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
        }
      };
    }, []);

    /**
     * Fetch all catalogs
     */
    useEffect(() => {
      fetchAllCatalogs({
        variables: {
          limit: 1,
          cursor: null,
          key: '',
          filterItem: 'all',
        },
        onCompleted: ({ fetchContentLibraryCatalogsItems }) => {
          if (fetchContentLibraryCatalogsItems?.catalogs?.length === 0 && !haveActiveIntegrations) {
            setEmptyCatalog(true);
            onSelectTreeItem({ key: 'default-deliverables' });
          }
        },
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Refetch catalogs
     *
     * @param {Object} params
     * @param {string} params.key
     * @param {string} params.search
     * @param {Array} params.filter
     * @param {string} params.sort
     */
    const refetchCatalogs = useCallback(
      async ({ key }) => {
        if (user?.paymentStatus?.canLock) {
          return;
        }
        const refetchCatalogData = await refetchAllCatalogs?.({
          limit: CATALOGS_LIMIT * (window.innerHeight >= 1250 ? 2 : 1),
          cursor: null,
          filterItem: 'all',
          key: key || selectedFolder.key,
        });

        if (refetchCatalogData?.data?.fetchContentLibraryCatalogsItems) {
          handleCatalogData(refetchCatalogData?.data?.fetchContentLibraryCatalogsItems);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        user?.paymentStatus?.canLock,
        refetchAllCatalogs,
        selectedFolder.key,
        selectedFolder.name,
        chosen,
        handleCatalogData,
      ]
    );

    /**
     * Fetch more catalogs
     *
     * @param {string} type
     */
    const fetchMoreCatalogs = async (type = '') => {
      if (hasNext && cursor && fetchMore && !isLoadingMore) {
        setIsLoadingMore(true);

        const fetchMoreCatalogData = await fetchMore({
          variables: {
            limit: CATALOGS_LIMIT * (window.innerHeight >= 1250 ? 2 : 1),
            cursor: cursor,
            key: selectedFolder.key,
            filterItem: 'all',
          },
        });

        if (fetchMoreCatalogData?.data?.fetchContentLibraryCatalogsItems) {
          handleCatalogData(
            fetchMoreCatalogData?.data?.fetchContentLibraryCatalogsItems,
            selectedFolder,
            true
          );
        }
        setIsLoadingMore(false);
      }
    };

    /**
     * Move catalog item
     */
    const [moveCatalogItem] = useMutation(CONTENT_LIBRARY_ADD_ITEM, {
      onCompleted() {
        notification.success({
          description: 'Item moved successfully.',
        });
        if (refetchCatalogs) {
          refetchCatalogs({ key: selectedFolder.key });
        }
      },
      onError() {
        notification.error({
          description: 'Item moved failed.',
        });
      },
    });

    /**
     * Delete catalog
     */
    const [deleteCatalog] = useMutation(CONTENT_LIBRARY_CATALOG_DELETE, {
      onCompleted() {
        setCatalogs(catalogs?.filter((catalog) => catalog._id !== selectedCatalog._id));
        notification.success({
          description: 'Product deleted successfully',
        });
        setConfirmModal(false);
        setSelectedCatalog(null);
        refetchCatalogs({ key: selectedFolder.key });
        setSelectedCatalog(null);
      },
      onError() {
        notification.error({
          description: 'Product deletion failed',
        });
        setConfirmModal(false);
        setSelectedCatalog(null);
      },
    });

    /**
     * Use imperative handle for refetching catalogs
     */
    useImperativeHandle(ref, () => ({
      refetchCatalogs,
    }));

    /**
     * On select tree item
     *
     * @param {Object} selectedKeys
     */
    const onSelectTreeItem = (selectedKeys, updatedPrelist) => {
      updatedPrelist = updatedPrelist || prelist;
      setSelectedFolderLoading(true);

      if (selectedKeys.key === 'default-deliverables') {
        const defaultDeliverables = updatedPrelist.find(
          (item) => item.key === 'defaultDeliverables'
        );

        defaultDeliverables['defaultDeliverables'].forEach((dS) => {
          _.forOwn(chosen, (chosendata) =>
            chosendata.list?.forEach((l) => {
              l.name === dS.name ? (dS.selected = true) : '';
            })
          );
        });

        defaultDeliverables.key = 'defaultDeliverables';
        updatefilteredprelist([defaultDeliverables]);
        setSelectedFolder({
          key: 'defaultDeliverables',
          name: 'Default Deliverables',
          totalItems: defaultDeliverables.totalItems,
        });
      } else if (selectedKeys.key === 'wix-services') {
        const wixServices = updatedPrelist.find((item) => item.key === 'wixServices');

        wixServices['wixServices'].forEach((wS) => {
          _.forOwn(chosen, (chosendata) =>
            chosendata.list?.forEach((l) => {
              l.name === wS.name && l.description === wS.description ? (wS.selected = true) : '';
            })
          );
        });
        wixServices.key = 'wixServices';
        updatefilteredprelist([wixServices]);
        setSelectedFolder({
          key: 'wixServices',
          name: 'Your Wix Services',
          totalItems: wixServices.totalItems,
        });
      } else if (selectedKeys.key === 'wix-products') {
        const wixProducts = updatedPrelist.find((item) => item.key === 'wixProducts');

        wixProducts['wixProducts'].forEach((wP) => {
          _.forOwn(chosen, (chosendata) =>
            chosendata.list?.forEach((l) => {
              l.name === wP.name && l.description === wP.description ? (wP.selected = true) : '';
            })
          );
        });
        wixProducts.key = 'wixProducts';
        updatefilteredprelist([wixProducts]);
        setSelectedFolder({
          key: 'wixProducts',
          name: 'Your Wix Products',
          totalItems: wixProducts.totalItems,
        });
      } else if (selectedKeys.key === 'pipedrive-products') {
        const pipedriveProducts = updatedPrelist.find((item) => item.key === 'pipedriveProducts');

        pipedriveProducts['pipedriveProducts'].forEach((pP) => {
          _.forOwn(chosen, (chosendata) =>
            chosendata.list?.forEach((l) => {
              l.name === pP.name ? (pP.selected = true) : '';
            })
          );
        });

        pipedriveProducts.key = 'pipedriveProducts';
        updatefilteredprelist([pipedriveProducts]);

        setSelectedFolder({
          key: 'pipedriveProducts',
          name: 'Your Pipedrive Products',
          totalItems: pipedriveProducts.totalItems,
          nextCursor: pipedriveProducts.nextCursor,
          hasMore: pipedriveProducts.hasMore,
        });
      } else if (selectedKeys.key === 'hubspot-products') {
        const hubspotProductsData = updatedPrelist.find((item) => item.key === 'hubspotProducts');

        hubspotProductsData['hubspotProducts'].forEach((hP) => {
          _.forOwn(chosen, (chosendata) =>
            chosendata.list?.forEach((l) => {
              l.name === hP.name ? (hP.selected = true) : '';
            })
          );
        });

        hubspotProductsData.key = 'hubspotProducts';
        updatefilteredprelist([hubspotProductsData]);

        setSelectedFolder({
          key: 'hubspotProducts',
          name: 'Your HubSpot Products',
          totalItems: hubspotProductsData.totalItems,
          nextCursor: hubspotProductsData.nextCursor,
          hasMore: hubspotProductsData.hasMore,
        });
      } else {
        if (selectedKeys.key === 'newTemplateFolder') {
          return;
        }

        const selectedFolderData = {
          key: selectedKeys.key || '',
          name:
            selectedKeys.name ||
            selectedKeys.key
              .split('-')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ') ||
            'All',
        };
        setSelectedFolder(selectedFolderData);

        fetchAllCatalogs({
          variables: {
            limit: CATALOGS_LIMIT * (window.innerHeight >= 1250 ? 2 : 1),
            cursor: null,
            key: selectedKeys.key,
            filterItem: 'all',
          },
          onCompleted: ({ fetchContentLibraryCatalogsItems }) => {
            if (fetchContentLibraryCatalogsItems?.catalogs) {
              handleCatalogData(fetchContentLibraryCatalogsItems, selectedFolderData);
            }
          },
          onError: () => {
            setSelectedFolderLoading(false);
          },
        });
      }
    };

    /**
     * On title click
     */
    const onTitleClick = () => {
      setSelectedFolder('');
      setfilteredprelist([]);
      setCatalogs([]);
      setCatalogsCount(0);
      setCursor(null);
      setHasNext(false);
    };

    useEffect(() => {
      if (!tempChosen && chosen) {
        setTempChosen({ ...chosen });
      }
    }, [chosen, tempChosen]);

    useEffect(() => {
      const tempPrelist = [];
      if (chosen && (tabType === 'super' || tabType === 'miles')) {
        // return if chosen is empty
        if (Object.keys(chosen).length === 0) {
          return;
        }
        prelist.forEach((i) => {
          chosen['OPTION 1']?.list?.forEach((x) => {
            x.name === i.name ? (i.selected = true) : '';
          });
          tempPrelist.push(i);
        });

        updatefilteredprelist(tempPrelist);
      } else if (chosen && tabType === 'delivs' && !Object.keys(selectedFolder).length > 0) {
      } else if (!Object.keys(selectedFolder).length > 0) {
        updatefilteredprelist(prelist);
      } else if (chosen && tabType === 'delivs' && !_.isEqual(chosen, tempChosen)) {
        setTempChosen({ ...chosen });

        onSelectTreeItem(
          {
            key: selectedFolder?.key?.replace(/([a-z])([A-Z])/g, '$1-$2')?.toLowerCase(),
            name: selectedFolder?.name,
          },
          [
            {
              name: selectedFolder?.name,
              key: selectedFolder?.key,
              [selectedFolder?.key]:
                catalogs?.length > 0
                  ? catalogs
                  : [
                      ...((prelist.find((item) => item.key === selectedFolder?.key) || [])?.[
                        selectedFolder?.key
                      ] || []),
                    ],
              totalItems: selectedFolder.totalItems,
              nextCursor: selectedFolder.nextCursor || selectedFolder.cursor,
              hasMore: selectedFolder.hasMore || selectedFolder.hasNext,
              hasNext: selectedFolder.hasNext || selectedFolder.hasMore,
              cursor: selectedFolder.cursor || selectedFolder.nextCursor,
            },
          ]
        );
      } else if (chosen && tabType === 'delivs' && Object.keys(selectedFolder).length > 0) {
        const selectedPrelist = prelist.find((item) => item.key === selectedFolder?.key);
        if (selectedPrelist?.totalItems > selectedFolder.totalItems) {
          updatefilteredprelist([selectedPrelist]);
        }
      }
    }, [prelist, chosen, newItemName]); //eslint-disable-line

    /**
     * On add new
     *
     * @param {string} name
     */
    const onAddNew = (name) => {
      name = sanitizeString(name).trim();
      if (!name.length) {
        return;
      }

      addNewItem(name);
      setNewPrelistItem(null);
      filterDelivs('');

      if (tabType !== 'delivs') {
        setNewItemName(name);
      }

      // remove from deleted array
      setDeletedItems((prev) => prev.filter((item) => item !== name));

      if (selectedFolder?.key) {
        // update selected folder
        onSelectTreeItem(
          {
            key: selectedFolder?.key?.replace(/([a-z])([A-Z])/g, '$1-$2')?.toLowerCase(),
          },
          [
            {
              name: selectedFolder?.name,
              key: selectedFolder?.key,
              [selectedFolder?.key]: [
                ...((prelist.find((item) => item.key === selectedFolder?.key) || [])?.[
                  selectedFolder?.key
                ] || []),
                {
                  name: name,
                  selected: true,
                  editable: true,
                  key: name,
                },
              ],
              totalItems: selectedFolder.totalItems,
              nextCursor: selectedFolder.nextCursor || selectedFolder.cursor,
              hasMore: selectedFolder.hasMore || selectedFolder.hasNext,
              hasNext: selectedFolder.hasNext || selectedFolder.hasMore,
              cursor: selectedFolder.cursor || selectedFolder.nextCursor,
            },
          ]
        );
      }
    };

    /**
     * On delete prelist item
     *
     * @param {string} name
     */
    const onDeletePrelistItem = (name) => {
      setDeletedItems((prev) => {
        const newData = [...new Set([...prev, name])];
        // remove from new item
        if (name === newItemName) {
          setNewItemName('');
        }
        // remove deleted items from array
        updatefilteredprelist(filteredprelist, newData);
        return newData;
      });

      removeItem(name);
    };

    /**
     * Load more wix products and services
     *
     * @param {string} type
     * @param {number} totalItems
     */
    const loadMoreWixProductsAndServices = (type, totalItems) => {
      fetchMoreWixProductsAndServices({
        variables: {
          instanceId: wixConnectedInstance,
          type: type || 'products',
          offset: (Math.floor(totalItems / 50) + 1 - 1) * 50,
        },
      });
    };

    /**
     * Load more HubSpot products
     *
     * @param {string} nextCursor Cursor for pagination
     */
    const loadMoreHubSpotProducts = (nextCursor) => {
      fetchMoreHubSpotProducts({
        variables: {
          cursor: nextCursor,
          limit: 50,
        },
      });
    };

    /**
     * Load more Pipedrive products
     *
     * @param {string} nextCursor Cursor for pagination
     */
    const loadMorePipedriveProducts = (nextCursor) => {
      fetchMorePipedriveProducts({
        variables: {
          cursor: nextCursor,
          limit: 50,
        },
      });
    };

    /**
     * On cancel duplicate catalog
     */
    const onCancelDuplicateCatalog = () => {
      setShowDuplicateCatalog(false);
      setSelectedCatalog(null);
      setSelectedFolderItemKey('');
      setSelectedFolderKey('');
    };

    /**
     * On key select
     *
     * @param {Array} folderKey
     */
    const onKeySelect = (folderKey) => {
      setSelectedFolderKey(folderKey?.[0]?.[0] || '');
      setSelectedFolderItemKey(folderKey?.[0]?.[0] || '');
    };

    /**
     * Cancel confirm modal
     */
    const onCancelConfirmModal = () => {
      setConfirmModal(false);
      setSelectedCatalog(null);
    };

    /**
     * Confirm delete catalog
     */
    const confirmDeleteCatalog = () => {
      deleteCatalog({
        variables: {
          _id: selectedCatalog._id,
          folderKey: selectedFolder.key,
        },
      });
    };

    /**
     * Duplicate item
     *
     * @param {Object} item
     */
    const duplicateItem = (item) => {
      setShowDuplicateCatalog(true);
      setSelectedCatalog(item);
      onKeySelect([[selectedFolder.key]]);
    };

    /**
     * Rename item
     *
     * @param {Object} item
     * @param {Object} selectedFolderItem
     */
    const renameItem = (item, selectedFolderItem) => {
      // setShowRenameCatalog(true);
      setIsRenamingCatalogItem(true);
      setSelectedCatalog(item);
      setSelectedFolder(selectedFolderItem);
    };

    /**
     * Save catalog rename
     *
     * @param {Object} item
     * @param {string} name
     */
    const saveCatalogRename = (item, name) => {
      setIsRenamingCatalogItem(false);
      setSelectedCatalog(null);

      name = sanitizeString(name).trim();

      if (!name.length || item.name === name) {
        return;
      }

      saveDeliverable({ ...item, name }, () => {
        setInitialDeliverableName('');
      });
    };

    /**
     * Render list items
     *
     * @param {Object} item
     */
    const renderListItems = (item) => {
      let listItemInfo = {
        innerDataSource: [],
        onClickAction: () => {},
        innerIconComponent: '',
        visibility: false,
        title: '',
        listClassName: '',
      };

      if (item.name && tabType === 'delivs') {
        listItemInfo = {
          title: item.name,
          innerDataSource: item[item.key],
          onClickAction: item.isSearchResult ? () => {} : onTitleClick,
          innerIconComponent: emptyCatalog ? '' : <ChevronRight />,
          visibility: Object.keys(selectedFolder).length > 0,
          listClassName: 'tablist-prelist-selected-folder',
        };
      } else if (item.name) {
        listItemInfo = {
          title: (
            <>
              {item.logo && <img alt="Wix" src={item['logo']} />}
              {item.name}
            </>
          ),
          onClickAction:
            item.selected && tabType === 'super'
              ? () => {}
              : (value) => {
                  addItem({ name: item.name })(value, chosen);

                  if (tabType === 'delivs') {
                    onSelectTreeItem(
                      {
                        key: selectedFolder.key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase(),
                      },
                      [
                        {
                          name: selectedFolder.name,
                          key: selectedFolder.key,
                          [selectedFolder.key]: [
                            ...prelist.find((item) => item.key === selectedFolder.key)?.[
                              selectedFolder.key
                            ],
                            {
                              name: item.name,
                              selected: true,
                              editable: true,
                              key: item.name,
                            },
                          ],
                          totalItems: selectedFolder.totalItems,
                          nextCursor: selectedFolder.nextCursor || selectedFolder.cursor,
                          hasMore: selectedFolder.hasMore || selectedFolder.hasNext,
                          hasNext: selectedFolder.hasNext || selectedFolder.hasMore,
                          cursor: selectedFolder.cursor || selectedFolder.nextCursor,
                        },
                      ]
                    );
                  }
                },
          innerIconComponent: (
            <>
              {item.editable && (
                <>
                  <DeleteIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeletePrelistItem(item.name);
                    }}
                  />
                  <Divider type="vertical" />
                </>
              )}
              {item.selected && <UnlinedTick />}
            </>
          ),
          visibility: false,
          listClassName: item.selected ? 'selected' : '',
        };
      } else {
        return <></>;
      }

      return (
        <>
          <List.Item
            key={item.name}
            className={listItemInfo.listClassName}
            onClick={listItemInfo.onClickAction}>
            <List.Item.Meta title={listItemInfo.title} />
            {listItemInfo.innerIconComponent}
          </List.Item>
          {listItemInfo.visibility && (
            <List
              dataSource={listItemInfo.innerDataSource}
              loadMore={
                (
                  item?.key === 'pipedriveProducts'
                    ? item?.hasMore
                    : item?.key === 'hubspotProducts'
                    ? item?.hasMore
                    : item?.hasNext || listItemInfo.innerDataSource?.length < item?.totalItems
                ) ? (
                  <Button
                    type="default"
                    size="medium"
                    className="tablist-prelist-load-more"
                    text="Load more"
                    icon={<DownloadCircleIcon />}
                    loading={
                      isLoadingMore ||
                      fetchMoreWixProductsAndServicesLoading ||
                      fetchMoreHubSpotProductsLoading ||
                      fetchMorePipedriveProductsLoading
                    }
                    onClick={() => {
                      if (item?.key === 'wixServices' || item?.key === 'wixProducts') {
                        loadMoreWixProductsAndServices(
                          listItemInfo?.type,
                          listItemInfo.innerDataSource?.length
                        );
                      } else if (item?.key === 'hubspotProducts') {
                        loadMoreHubSpotProducts(
                          item?.nextCursor || '' // Use cursor from item data
                        );
                      } else if (item?.key === 'pipedriveProducts') {
                        loadMorePipedriveProducts(
                          item?.nextCursor || '' // Use cursor from item data
                        );
                      } else {
                        fetchMoreCatalogs();
                      }
                    }}
                  />
                ) : null
              }
              renderItem={(innerItem, innerItemIndex) => (
                <List.Item
                  key={`${innerItem.name}-${innerItemIndex}`}
                  onClick={
                    innerItem.selected && tabType === 'super'
                      ? () => {}
                      : (value) => {
                          addItem({
                            name: innerItem?.name,
                            description: innerItem?.description,
                            price: innerItem?.price?.amount,
                            type: innerItem?.price?.type,
                            images: innerItem?.images,
                            discount: innerItem?.discount,
                            items: innerItem?.price?.items,
                            quantity: innerItem?.price?.quantity,
                          })(value, chosen);

                          if (tabType === 'delivs') {
                            onSelectTreeItem(
                              {
                                key: selectedFolder.key
                                  .replace(/([a-z])([A-Z])/g, '$1-$2')
                                  .toLowerCase(),
                                name: selectedFolder.name,
                              },
                              [
                                {
                                  name: selectedFolder.name,
                                  key: selectedFolder.key,
                                  [selectedFolder.key]:
                                    catalogs?.length > 0
                                      ? catalogs.map((item) =>
                                          item.name === innerItem.name
                                            ? {
                                                ...item,
                                                selected: true,
                                                editableCatalog: true,
                                                name: innerItem.name,
                                              }
                                            : {
                                                ...item,
                                                editableCatalog: true,
                                              }
                                        )
                                      : [
                                          ...prelist
                                            .find((item) => item.key === selectedFolder.key)
                                            ?.[selectedFolder.key]?.map((item) =>
                                              item.name === innerItem.name
                                                ? {
                                                    ...item,
                                                    selected: true,
                                                    name: innerItem.name,
                                                  }
                                                : item
                                            ),
                                        ],
                                },
                              ]
                            );
                          }
                        }
                  }
                  className={innerItem?.selected ? 'selected' : ''}>
                  <List.Item.Meta
                    title={
                      innerItem?._id === selectedCatalog?._id && isRenamingCatalogItem ? (
                        <Input
                          autoFocus
                          defaultValue={innerItem?.name}
                          size="small"
                          onBlur={(e) => saveCatalogRename(innerItem, e.target.value)}
                          onPressEnter={(e) => saveCatalogRename(innerItem, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <Tooltip title={innerItem?.name}>{innerItem?.name}</Tooltip>
                      )
                    }
                    avatar={
                      innerItem?.images?.[0]?.url || innerItem?.image ? (
                        <Avatar
                          src={innerItem?.images?.[0]?.url || innerItem?.image}
                          alt={innerItem?.images?.[0]?.name || innerItem?.name || 'product'}
                          size={20}
                        />
                      ) : null
                    }
                  />
                  {innerItem.editableCatalog &&
                    innerItem?._id === selectedCatalog?._id &&
                    isRenamingCatalogItem && (
                      <>
                        <SaveIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            saveCatalogRename(innerItem, e.target.value);
                          }}
                        />
                      </>
                    )}
                  {innerItem.editableCatalog &&
                    innerItem?._id !== selectedCatalog?._id &&
                    !isRenamingCatalogItem && (
                      <Dropdown
                        overlayClassName="tablist-prelist-item-menu-dropdown"
                        overlay={
                          <Menu>
                            <Menu.SubMenu
                              title="Move"
                              key="move"
                              visible={true}
                              disabled={filteredTreeData?.children?.length < 2}
                              popupClassName="catalog-list__dropdown-menu-move">
                              <div
                                className="catalog-list__dropdown-content"
                                onClick={(e) => e.stopPropagation()}>
                                <div className="dropdown-content__header">Move product</div>
                                <Divider className="divider" />
                                <ContentLibraryTree
                                  onSelectTreeItem={(selectedTree) =>
                                    moveCatalogItem({
                                      variables: {
                                        type: 'catalogs',
                                        fromKey: selectedFolder.key || '',
                                        key: selectedTree.key,
                                        id: selectedTree.treeId,
                                        itemId: innerItem._id,
                                      },
                                    })
                                  }
                                  onSelect={() => {}}
                                  type="catalogs"
                                  onUpdate={() => {}}
                                  treeData={filteredTreeData || {}}
                                  disableNewFolder={true}
                                  fromModal={true}
                                  onKeySelect={() => {}}
                                />
                              </div>
                            </Menu.SubMenu>
                            <Menu.Item
                              key="rename"
                              onClick={(e) => {
                                e.domEvent.stopPropagation();
                                renameItem(innerItem, selectedFolder);
                              }}>
                              Rename
                            </Menu.Item>
                            <Menu.Item
                              key="duplicate"
                              onClick={(e) => {
                                e.domEvent.stopPropagation();
                                duplicateItem(innerItem);
                              }}>
                              Duplicate
                            </Menu.Item>
                            <Divider type="horizontal" />
                            <Menu.Item
                              key="delete"
                              danger
                              onClick={(e) => {
                                e.domEvent.stopPropagation();
                                setConfirmModal(true);
                                setSelectedCatalog(innerItem);
                              }}>
                              Delete
                            </Menu.Item>
                          </Menu>
                        }
                        trigger={['click']}>
                        <SliderIcon
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        />
                      </Dropdown>
                    )}

                  {innerItem.editable && (
                    <>
                      <DeleteIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeletePrelistItem(innerItem.name);
                        }}
                      />
                      <Divider type="vertical" />
                    </>
                  )}

                  {innerItem.selected && <UnlinedTick />}
                </List.Item>
              )}
              itemLayout="horizontal"
              locale={{
                emptyText: (
                  <Empty
                    description={
                      <span>
                        <p>
                          Save deliverables to your catalog or upload via your{' '}
                          <span
                            className="empty-description-link"
                            onClick={() => history.push('/contentlibrary?tab=6')}>
                            content library
                          </span>
                        </p>
                        <Divider type="horizontal" />
                      </span>
                    }
                    image={images.DEFAULT_DELIVERABLES}
                  />
                ),
              }}
            />
          )}
        </>
      );
    };

    /**
     * Get the original source data for the selected folder
     *
     * @returns {Array}
     */
    const getOriginalSourceData = useCallback(() => {
      if (
        selectedFolder.key === 'defaultDeliverables' ||
        selectedFolder.key === 'wixServices' ||
        selectedFolder.key === 'wixProducts' ||
        selectedFolder.key === 'pipedriveProducts' ||
        selectedFolder.key === 'hubspotProducts'
      ) {
        const originalItem = prelist?.find((item) => item.key === selectedFolder.key);
        return originalItem?.[selectedFolder.key] || [];
      } else {
        // For catalog lists, use catalogs if available, otherwise fall back to prelist
        if (catalogs?.length > 0) {
          return catalogs;
        }
        const originalItem = prelist?.find((item) => item.key === selectedFolder.key);
        return originalItem?.[selectedFolder.key] || [];
      }
    }, [catalogs, prelist, selectedFolder.key]);

    /**
     * Filter delivs (actual implementation)
     *
     * @param {string} delivsRegex
     */
    const filterDelivsImpl = useCallback(
      (delivsRegex) => {
        // When clearing the input, restore the full list
        if (!delivsRegex || delivsRegex.trim() === '') {
          setSearchResults(null);
          searchResultsRef.current = null; // Clear ref
          const originalData = getOriginalSourceData();

          if (
            selectedFolder.key === 'defaultDeliverables' ||
            selectedFolder.key === 'wixServices' ||
            selectedFolder.key === 'wixProducts' ||
            selectedFolder.key === 'pipedriveProducts' ||
            selectedFolder.key === 'hubspotProducts'
          ) {
            setfilteredprelist([
              {
                name: selectedFolder.name,
                key: selectedFolder.key,
                [selectedFolder.key]: originalData,
              },
            ]);
          } else {
            // For catalog lists, restore using handleCatalogData
            handleCatalogData({ catalogs, catalogsCount });
          }
          return;
        }

        // Use the comprehensive search API for all deliverables
        // If a folder is selected, only search within that folder
        const searchFolderKey = Object.keys(selectedFolder).length > 0 ? selectedFolder.key : null;

        searchAllDeliverables({
          variables: {
            search: delivsRegex.trim(),
            limit: 50,
            folderKey: searchFolderKey,
          },
          onCompleted: ({ searchAllDeliverables: searchData }) => {
            if (searchData?.items?.length > 0) {
              setSearchResults(searchData);
              searchResultsRef.current = searchData; // Preserve in ref

              // If no folder is selected, show tree structure with folders containing results
              if (!searchFolderKey) {
                // Transform search results into tree structure for ContentLibraryTree
                const folderMap = new Map();

                searchData.items.forEach((item) => {
                  const folderKey = item.folderKey || 'search-result';
                  if (!folderMap.has(folderKey)) {
                    folderMap.set(folderKey, {
                      name: item.folderName,
                      key: folderKey,
                      [folderKey]: [],
                      isSearchResult: true,
                      sourceType: item.sourceType,
                    });
                  }

                  const product = {
                    name: item.name,
                    image: item.image,
                    editable: item.sourceType === 'defaultDeliverables',
                    sourceType: item.sourceType,
                  };

                  _.forOwn(chosen, (chosendata) =>
                    chosendata.list?.forEach((l) => {
                      l.name === product.name ? (product.selected = true) : '';
                    })
                  );

                  folderMap.get(folderKey)[folderKey].push(product);
                });

                const searchPrelist = Array.from(folderMap.values());
                setfilteredprelist(searchPrelist);
              } else {
                // Folder is selected - show results directly in that folder
                const products = searchData.items.map((item) => {
                  const product = {
                    name: item.name,
                    image: item.image,
                    editable: item.sourceType === 'defaultDeliverables',
                    sourceType: item.sourceType,
                  };

                  _.forOwn(chosen, (chosendata) =>
                    chosendata.list?.forEach((l) => {
                      l.name === product.name ? (product.selected = true) : '';
                    })
                  );

                  return product;
                });

                setfilteredprelist([
                  {
                    name: selectedFolder.name,
                    key: selectedFolder.key,
                    [selectedFolder.key]: products,
                    isSearchResult: true,
                  },
                ]);
              }
            } else {
              // No results found
              setSearchResults({ items: [] });
              searchResultsRef.current = { items: [] }; // Preserve in ref
              setfilteredprelist([]);
            }
          },
          onError: () => {
            setSearchResults(null);
            searchResultsRef.current = null; // Clear ref
          },
        });
      },
      [
        selectedFolder,
        catalogs,
        catalogsCount,
        chosen,
        searchAllDeliverables,
        handleCatalogData,
        getOriginalSourceData,
      ]
    );

    /**
     * Filter delivs with debounce
     * Creates a debounced version on first call, then reuses it
     *
     * @param {string} delivsRegex
     */
    const filterDelivs = useCallback(
      (delivsRegex) => {
        // If debounced function doesn't exist yet, create it
        if (!debouncedFilterDelivsRef.current) {
          debouncedFilterDelivsRef.current = _.debounce((searchValue) => {
            // Clear debouncing state when the debounced function executes
            setIsDebouncing(false);
            filterDelivsImpl(searchValue);
          }, 500); // 500ms debounce delay
        }

        // If search is empty, call immediately (no debounce for clearing)
        if (!delivsRegex || delivsRegex.trim() === '') {
          // Cancel any pending debounced calls
          if (debouncedFilterDelivsRef.current) {
            debouncedFilterDelivsRef.current.cancel();
          }
          setIsDebouncing(false); // Clear debouncing state
          filterDelivsImpl(delivsRegex);
        } else {
          // Show loading state while debouncing
          setIsDebouncing(true);
          // Use debounced version for non-empty searches
          debouncedFilterDelivsRef.current(delivsRegex);
        }
      },
      [filterDelivsImpl]
    );

    /**
     * Check if the selected folder is a catalog list
     */
    const isCatalogList =
      Object.keys(selectedFolder).length > 0 &&
      !['defaultDeliverables', 'wixServices', 'wixProducts'].includes(selectedFolder.key);

    /**
     * Filter tree to show only folders with search results
     * Also collects tree node keys for expanding folders
     *
     * @param {Object} tree - Tree data
     * @param {Array} searchItems - Search result items
     * @returns {Object} - Object with filteredTree and expandedKeys
     */
    const filterTreeBySearchResults = (tree, searchItems) => {
      if (!tree || !searchItems || searchItems.length === 0) {
        return { filteredTree: { children: [] }, expandedKeys: [] };
      }

      // Normalize: keep empty strings as empty strings (for uncategorized items)
      const folderKeys = new Set(
        searchItems.map((item) =>
          item.folderKey !== undefined && item.folderKey !== null ? item.folderKey : ''
        )
      );

      // Map of search folderKey variations to tree folder structure
      const specialFolderMap = {
        'default-deliverables': {
          key: 'default-deliverables',
          path: 'default-deliverables',
          defaultTitle: 'Default Deliverables',
        },
        defaultDeliverables: {
          key: 'default-deliverables',
          path: 'default-deliverables',
          defaultTitle: 'Default Deliverables',
        },
        'wix-services': { key: 'wix-services', path: 'wix-services', defaultTitle: 'Wix Services' },
        wixServices: { key: 'wix-services', path: 'wix-services', defaultTitle: 'Wix Services' },
        'wix-products': { key: 'wix-products', path: 'wix-products', defaultTitle: 'Wix Products' },
        wixProducts: { key: 'wix-products', path: 'wix-products', defaultTitle: 'Wix Products' },
        'pipedrive-products': {
          key: 'pipedrive-products',
          path: 'pipedrive-products',
          defaultTitle: 'Pipedrive Products',
        },
        pipedriveProducts: {
          key: 'pipedrive-products',
          path: 'pipedrive-products',
          defaultTitle: 'Pipedrive Products',
        },
        'hubspot-products': {
          key: 'hubspot-products',
          path: 'hubspot-products',
          defaultTitle: 'HubSpot Products',
        },
        hubspotProducts: {
          key: 'hubspot-products',
          path: 'hubspot-products',
          defaultTitle: 'HubSpot Products',
        },
      };

      const addedSpecialFolders = new Set();
      const specialFolders = [];
      const expandedKeys = [];

      // Group search items by folderKey
      const itemsByFolderKey = {};
      searchItems.forEach((item) => {
        // Use the actual folderKey, even if it's empty string (for uncategorized items)
        const key = item.folderKey !== undefined && item.folderKey !== null ? item.folderKey : '';
        if (!itemsByFolderKey[key]) {
          itemsByFolderKey[key] = [];
        }
        itemsByFolderKey[key].push(item);
      });

      folderKeys.forEach((folderKey) => {
        // Normalize folderKey for matching
        const normalizedFolderKey = folderKey !== undefined && folderKey !== null ? folderKey : '';
        const folderConfig = specialFolderMap[normalizedFolderKey];
        if (folderConfig && !addedSpecialFolders.has(folderConfig.key)) {
          const folderName =
            searchItems.find((item) => {
              const itemKey =
                item.folderKey !== undefined && item.folderKey !== null ? item.folderKey : '';
              return itemKey === normalizedFolderKey;
            })?.folderName || folderConfig.defaultTitle;

          // Get search items for this folder - use exact match
          const folderItems = itemsByFolderKey.hasOwnProperty(normalizedFolderKey)
            ? itemsByFolderKey[normalizedFolderKey] || []
            : [];

          // Create child nodes for search results
          const resultChildren =
            folderItems.length > 0
              ? folderItems.map((item, idx) => ({
                  key: `${folderConfig.key}_search_${idx}`,
                  path: `${folderConfig.path}_search_${idx}`,
                  title: item.name,
                  isLeaf: true,
                  isSearchResultItem: true,
                  searchItem: item,
                  itemId: item._id, // Add item ID for adding to pricing
                  icon: null,
                }))
              : null;

          // Don't add folder if it has no children
          if (!resultChildren || resultChildren.length === 0) {
            return;
          }

          const specialFolder = {
            children: resultChildren,
            key: folderConfig.key,
            path: folderConfig.path,
            title: folderName,
            default: true,
            isSearchResult: true,
            index: specialFolders.length,
            _searchKey: `search_${folderConfig.key}_${folderItems.length}`,
          };

          specialFolders.push(specialFolder);
          // Add special folder key to expandedKeys
          if (specialFolder.key) {
            expandedKeys.push(specialFolder.key);
          }
          addedSpecialFolders.add(folderConfig.key);
        }
      });

      // Filter catalog folders from tree and collect keys
      const filterChildren = (children, parentIndex = 0) => {
        if (!children || !Array.isArray(children)) return [];

        return children
          .map((child, idx) => {
            // Normalize path: empty string or undefined becomes empty string
            const childPath = child.path !== undefined && child.path !== null ? child.path : '';
            const childKey = child.key || '';

            // Normalize both sides for comparison
            const normalizedChildPath = childPath || '';
            const hasDirectResults = folderKeys.has(normalizedChildPath);

            // Check if any search results are in child folders of this folder
            const hasChildResults = Array.from(folderKeys).some((key) => {
              // Skip special folder keys (already handled above)
              if (specialFolderMap[key]) return false;

              // Normalize key for comparison
              const normalizedKey = key !== undefined && key !== null ? key : '';

              // Skip if key is empty (uncategorized items at root)
              if (!normalizedKey) return false;

              if (!normalizedChildPath) {
                return normalizedKey.startsWith('children.');
              }
              return normalizedKey.startsWith(normalizedChildPath + '.');
            });

            // Recursively filter children
            const filteredChildChildren =
              child.children && Array.isArray(child.children)
                ? filterChildren(child.children, idx)
                : [];
            const hasMatchingChildren = filteredChildChildren.length > 0;

            if (hasDirectResults || hasChildResults || hasMatchingChildren) {
              // Add this node's key to expandedKeys if it has direct results
              if (hasDirectResults && childKey) {
                expandedKeys.push(childKey);

                // Also expand parent folders by extracting parent paths
                if (childPath.includes('.')) {
                  const pathParts = childPath.split('.');
                  // Build parent paths progressively (e.g., 'children.0', 'children.0.children.1')
                  for (let i = 2; i < pathParts.length; i += 2) {
                    const parentPath = pathParts.slice(0, i + 1).join('.');
                    // Find the parent node key in the original tree
                    const findParentKey = (nodes) => {
                      for (const node of nodes || []) {
                        if (node.path === parentPath && node.key) {
                          return node.key;
                        }
                        if (node.children) {
                          const found = findParentKey(node.children);
                          if (found) return found;
                        }
                      }
                      return null;
                    };
                    const parentKey = findParentKey(tree.children);
                    if (parentKey && !expandedKeys.includes(parentKey)) {
                      expandedKeys.push(parentKey);
                    }
                  }
                }
              }

              // Use normalized path to match items - must be exact match
              const normalizedPath = childPath || '';
              const folderItems =
                hasDirectResults && itemsByFolderKey.hasOwnProperty(normalizedPath)
                  ? itemsByFolderKey[normalizedPath] || []
                  : [];

              // This ensures items are only added to folders that match their folderKey exactly
              const resultChildren =
                folderItems.length > 0
                  ? folderItems.map((item, itemIdx) => ({
                      key: `${childKey}_search_${itemIdx}`,
                      path: `${normalizedPath}_search_${itemIdx}`,
                      title: item.name,
                      isLeaf: true,
                      isSearchResultItem: true,
                      searchItem: item,
                      itemId: item._id, // Add item ID for adding to pricing
                      image: item.image,
                    }))
                  : [];

              const finalChildren =
                hasDirectResults && resultChildren.length > 0
                  ? resultChildren
                  : filteredChildChildren.length > 0
                  ? filteredChildChildren
                  : null;

              // Only include folder if it has direct results, child results, or matching children
              if (!hasDirectResults && !hasChildResults && !hasMatchingChildren) {
                return null;
              }

              // Create new children array reference even if it's the same content
              const newChildren = finalChildren
                ? Array.isArray(finalChildren)
                  ? [...finalChildren]
                  : finalChildren
                : null;

              // Don't include folder if it has no children
              if (!newChildren || (Array.isArray(newChildren) && newChildren.length === 0)) {
                return null;
              }

              return {
                ...child,
                children: newChildren,
                isSearchResult: hasDirectResults,
                index: child.index !== undefined ? child.index : idx,
                _searchKey: hasDirectResults
                  ? `search_${normalizedPath}_${folderItems.length}`
                  : undefined,
              };
            }
            return null;
          })
          .filter((child) => child !== null);
      };

      // Filter catalog children from tree
      const catalogChildren =
        tree.children && Array.isArray(tree.children) ? filterChildren(tree.children) : [];

      // Always create new array reference to ensure React detects changes
      const allChildren = [...specialFolders, ...catalogChildren];

      const filtered = {
        ...tree,
        children: allChildren,
        _searchTimestamp: Date.now(),
        _hasSearchResults: searchItems.length > 0,
      };

      return {
        filteredTree: filtered,
        expandedKeys: [...new Set(expandedKeys)], // Remove duplicates
      };
    };

    /**
     * Render handle tree search
     *
     * @param {string} tempSearchValue
     */
    const renderHandleTreeSearch = (tempSearchValue) =>
      handleModalTreeSearchWrapper({
        tempSearchValue,
        treeData: filteredTreeData,
        setIndexes,
        initialTreeData,
        addKey,
        setModalTreeData,
        filteredTreeData,
      });

    /**
     * Render add new child from search
     *
     * @param {string} treeSearchValue
     */
    const renderAddNewChildFromSearch = (treeSearchValue) =>
      addNewChildFromSearch({
        treeSearchValue,
        selectedFolderKey,
        user,
        treeData: modalTreeData,
        addChild,
      });

    const toggleNewDeliverablePopup = (value) => {
      if (value) {
        setInitialDeliverableName(value);
      } else {
        filterDelivs('');
        setNewPrelistItem(null);
        setInitialDeliverableName('');
      }
      setShowNewDeliverablePopup(!showNewDeliverablePopup);
    };

    // Use ref to preserve search results for tree filtering even if state was cleared
    const currentSearchResults = searchResults || searchResultsRef.current;
    const filterResult =
      currentSearchResults && currentSearchResults.items
        ? filterTreeBySearchResults(treeData, currentSearchResults.items)
        : { filteredTree: treeData, expandedKeys: [] };

    const updatedFilteredTreeData = filterResult.filteredTree;
    const selectedDeliverablesFolderKeys = filterResult.expandedKeys;

    /**
     * Render prelist component
     */
    return (
      <Col className="tablist-prelist">
        <Row className="tablist-prelist-input">
          <Select
            showSearch
            autoClearSearchValue={true}
            mode="multiple"
            showArrow={false}
            filterOption={false}
            notFoundContent={null}
            popupClassName="tablist-prelist-input-popup"
            getPopupContainer={() => document.querySelector('.tablist-prelist-input')}
            value={[] || undefined}
            onDropdownVisibleChange={() => {
              if (
                !currentSearchResults?.items?.length &&
                newPrelistItem &&
                !searchAllLoading &&
                !isDebouncing
              ) {
                filterDelivs('');
                setNewPrelistItem(null);
              }
            }}
            options={
              !currentSearchResults?.items?.length &&
              newPrelistItem &&
              !searchAllLoading &&
              !isDebouncing
                ? [
                    {
                      value: newPrelistItem,
                      label: (
                        <>
                          Add <strong>{newPrelistItem}</strong> as deliverable
                        </>
                      ),
                    },
                  ]
                : null
            }
            onKeyDown={(e) => {
              if (isCatalogList || tabType === 'delivs') {
                return;
              }
              if (e.keyCode === 13) {
                onAddNew(e.target.value);
              }
            }}
            suffixIcon={
              tabType === 'delivs' && (isDebouncing || searchAllLoading) ? (
                <Spin size="small" />
              ) : !isCatalogList ? (
                <AddBlockIcon
                  onClick={() => {
                    onAddNew(newPrelistItem);
                  }}
                />
              ) : null
            }
            onSearch={(searchValue) => {
              // Always update the input value immediately for better UX
              if (tabType === 'delivs') {
                setNewPrelistItem(searchValue, tabType);
                // Use debounced search for deliverables to avoid multiple requests
                filterDelivs(searchValue);
              } else {
                handleState(tabType, searchValue);
                if (tabType !== 'delivs') {
                  setNewItemName('');
                }
              }
            }}
            onSelect={(value) => {
              if (value && !currentSearchResults?.items?.length) {
                if (tabType === 'delivs' && value.length > 0) {
                  toggleNewDeliverablePopup(value?.trim());
                } else {
                  onAddNew(value);
                }
              }
              filterDelivs('');
              setNewPrelistItem(null);
              setNewItemName('');
            }}
            onBlur={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Clear any existing timeout
              if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
              }

              // Check if the blur is happening because of a click on the tree
              const { relatedTarget } = e;
              const isClickingOnTree =
                relatedTarget &&
                (relatedTarget.closest('.tablist-prelist-content') ||
                  relatedTarget.closest('.ant-tree') ||
                  relatedTarget.closest('.content-library-tree'));

              // If clicking on tree, don't reset - let the click handler process first
              if (isClickingOnTree || isTreeClickRef.current) {
                isTreeClickRef.current = false;
                return;
              }

              // Delay the reset to allow click events to process first
              blurTimeoutRef.current = setTimeout(() => {
                // Double-check that we're not clicking on tree
                if (isTreeClickRef.current) {
                  isTreeClickRef.current = false;
                  return;
                }

                const tempPrelist = [];

                prelist.forEach((i) => {
                  chosen['OPTION 1']?.list?.forEach((x) => {
                    x.name === i.name ? (i.selected = true) : '';
                  });
                  tempPrelist.push(i);
                });

                updatefilteredprelist(tempPrelist);

                handleState(tabType, '');
                setNewPrelistItem(null);
                setNewItemName('');
                filterDelivs('');
              }, 150);
            }}
            allowClear={true}
            onClear={() => {
              // Only clear search results when user explicitly clears the input
              if (tabType === 'delivs') {
                filterDelivs('');
              } else {
                // Clear regex for non-delivs types when input is cleared
                handleState(tabType, '');
              }
              setNewPrelistItem(null);
              setNewItemName('');
            }}
            placeholder={`Search ${tabType !== 'delivs' ? 'or Add Item (↵ to add)' : 'Item'}`}
          />

          {tabType === 'delivs' && (
            <div className="footer-button">
              <Button
                className="large-btn grey-btn"
                onClick={() => toggleNewDeliverablePopup()}
                text="New Deliverable"
                icon={<PlusThinIcon className="add-catalog-icon" />}
              />
            </div>
          )}
        </Row>

        {(showDuplicateCatalog || showNewDeliverablePopup) && (
          <MoveLibraryModal
            initialDeliverableName={initialDeliverableName}
            language={language}
            visible={showDuplicateCatalog || showNewDeliverablePopup}
            onCancel={showDuplicateCatalog ? onCancelDuplicateCatalog : toggleNewDeliverablePopup}
            catalog={selectedCatalog}
            selectedFolderKey={selectedFolderKey}
            selectedPrelistFolderKey={selectedFolderItemKey}
            refetchCatalogs={() => refetchCatalogs({ key: selectedFolder.key })}
            isCatalog={showNewDeliverablePopup ? 'Add-Deliverable' : 'Duplicate-Deliverable'}
            treeData={modalTreeData}
            catalogIds={selectedCatalogs}
            onKeySelect={onKeySelect}
            addNewChildFromSearch={renderAddNewChildFromSearch}
            handleTreeSearch={renderHandleTreeSearch}
            saveDeliverable={saveDeliverable}
          />
        )}

        {confirmModal && (
          <ConfirmModal
            title="Delete Product?"
            body="This action cannot be undone"
            visible={confirmModal}
            cancelText="CANCEL"
            confirmText="YES, DELETE"
            onCancel={onCancelConfirmModal}
            onConfirm={confirmDeleteCatalog}
          />
        )}

        {!currentSearchResults?.items?.length &&
          newPrelistItem &&
          !searchAllLoading &&
          !isDebouncing && (
            <div className="tablist-prelist-no-results">
              <Empty
                image={<EmptyFileIcon />}
                description={
                  <div>
                    <p>No results found</p>
                    <Divider type="horizontal" />
                  </div>
                }
              />
            </div>
          )}

        {(searchAllLoading || isDebouncing) && (
          <div className="tablist-prelist-loading">
            <Loader size="small" />
          </div>
        )}

        <Row className="tablist-prelist-content">
          {tabType === 'delivs' &&
          !Object.keys(selectedFolder).length &&
          !searchAllLoading &&
          !isDebouncing ? (
            <ContentLibraryTree
              key={`tree-${currentSearchResults?.items?.length || 0}-${
                updatedFilteredTreeData?._searchTimestamp || 0
              }-${JSON.stringify(selectedDeliverablesFolderKeys)}`}
              ref={contentLibraryTreeRef}
              onSelectTreeItem={(selectedKeys) => {
                // Mark that a tree click is happening to prevent blur from resetting
                isTreeClickRef.current = true;
                onSelectTreeItem(selectedKeys);
                // Reset the flag after a short delay
                setTimeout(() => {
                  isTreeClickRef.current = false;
                }, 200);
              }}
              onKeySelect={() => {}}
              type="catalogs"
              onUpdate={() =>
                refetchCatalogs({
                  key: selectedFolder.key,
                })
              }
              userId={user && user._id}
              userRole={user?.roles && user?.roles[0]}
              treeData={updatedFilteredTreeData}
              disableEditing={true}
              fromModal={true}
              selectedDeliverablesFolderKeys={selectedDeliverablesFolderKeys || []}
              hasSearchResults={
                !!(
                  currentSearchResults &&
                  currentSearchResults.items &&
                  currentSearchResults.items.length > 0
                )
              }
              isDefaultFolder={!!prelist.find((item) => item.name === 'defaultDeliverables')}
              isWixServices={!!prelist.find((item) => item.name === 'wixServices')}
              isWixProducts={!!prelist.find((item) => item.name === 'wixProducts')}
              isPipedriveProducts={!!prelist.find((item) => item.name === 'pipedriveProducts')}
              isHubSpotProducts={!!prelist.find((item) => item.name === 'hubspotProducts')}
              onSearchResultItemClick={(searchItem, itemId) => {
                // Mark that a tree click is happening to prevent blur from resetting
                isTreeClickRef.current = true;

                // Use ref to get search results even if state was cleared on blur
                const currentSearchResults = searchResults || searchResultsRef.current;

                // Only handle clicks when there are search results
                if (currentSearchResults?.items?.length > 0 && searchItem) {
                  // Use search item data directly to add to pricing table
                  // Convert images array to the format expected by addItem
                  const imagesArray =
                    searchItem.images && Array.isArray(searchItem.images)
                      ? searchItem.images.map((img) => ({
                          name: img.name || '',
                          url: img.url || '',
                          src: img.url || '',
                        }))
                      : searchItem.image
                      ? [{ name: '', url: searchItem.image, src: searchItem.image }]
                      : [];

                  addItem({
                    name: searchItem.name || '',
                    description: searchItem.description || '',
                    price: searchItem.price?.amount || '0',
                    type: searchItem.price?.type || '',
                    images: imagesArray,
                    discount: searchItem.discount,
                    items: searchItem.price?.items || 1,
                    quantity: searchItem.price?.quantity || 1,
                  })(null, chosen);
                  setNewPrelistItem(null);
                  filterDelivs('');

                  // Reset the flag after a short delay
                  setTimeout(() => {
                    isTreeClickRef.current = false;
                  }, 200);
                }
              }}
            />
          ) : !searchAllLoading && !isDebouncing ? (
            <List
              loading={selectedFolderLoading || searchAllLoading || isDebouncing}
              dataSource={filteredprelist}
              renderItem={renderListItems}
            />
          ) : null}
        </Row>
      </Col>
    );
  }
);

Prelist.defaultProps = {
  chosen: '',
  wixConnectedInstance: '',
  fetchMoreWixProductsAndServicesLoading: false,
  fetchMorePipedriveProductsLoading: false,
  fetchMoreHubSpotProductsLoading: false,
  haveActiveIntegrations: false,
  fetchMoreWixProductsAndServices: () => {},
  fetchMorePipedriveProducts: () => {},
  fetchMoreHubSpotProducts: () => {},
};

Prelist.propTypes = {
  prelist: PropTypes.instanceOf(Array).isRequired,
  chosen: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  tabType: PropTypes.string.isRequired,
  addItem: PropTypes.func.isRequired,
  removeItem: PropTypes.func.isRequired,
  addNewItem: PropTypes.func.isRequired,
  handleState: PropTypes.func.isRequired,
  fetchMoreWixProductsAndServicesLoading: PropTypes.bool,
  fetchMoreWixProductsAndServices: PropTypes.func,
  fetchMorePipedriveProductsLoading: PropTypes.bool,
  fetchMorePipedriveProducts: PropTypes.func,
  fetchMoreHubSpotProductsLoading: PropTypes.bool,
  fetchMoreHubSpotProducts: PropTypes.func,
  wixConnectedInstance: PropTypes.string,
  haveActiveIntegrations: PropTypes.bool,
};

export default Prelist;
